import React, { useContext, useEffect, useMemo } from "react";
import cl from "classnames";
import {
    EmailIcon,
    PeopleIcon,
    PhoneIcon,
    UserIcon,
} from "@assets/icons";
import { faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';

import { Controller, useFormContext } from "react-hook-form";
import BookedVenue from "@components/BookedVenue";
import Input from "@ui/Input";
import InputGroup from "@ui/InputGroup";
import ClientOnly from "../../ClientOnly";
import useOpenClose from "@hooks/useOpenClose";

import { EnquiryFormContext, SchemaContext } from "./EnquiryForm";
import { UserSectionProps } from "./UserSection";
import { FormValidationType } from "./schema";

import css from "./EnquiryFormUserDetails.module.scss";
import { formatDate } from "@helper/dateHelper";

const UserSection = React.lazy(() => import('./UserSection'));

const SECTION_FIELDS:Partial<keyof FormValidationType>[] = ["name", "email", "phone", "guests", "date", "engagementDate", "venue"];

const useSectionFields = (): Partial<keyof FormValidationType>[] => {
    const { hideGuest } = useContext(EnquiryFormContext);

    const fields = useMemo(() => hideGuest ? SECTION_FIELDS.filter((field) => field !== 'guests') : SECTION_FIELDS, [hideGuest]);
    return fields;
}

/**
 * Return a schema for the User Details section fields
 * @returns 
 */
const useSectionSchema = () => {
    const schema = useContext(SchemaContext);
    const fields = useSectionFields();
    const sectionSchema = useMemo(() => {
        const sectionFields = fields
            .reduce((acc, field) => ({
                ...acc,
                [field]: true,
            }), {} as Record<keyof FormValidationType, true>);
        return schema.pick(sectionFields);
    }, [fields, schema]);

    return sectionSchema;
}

/**
 * Collapsible section for the User Details section
 * Expand section on submiting if any field is invalid
 * @returns 
 */
const useCollapsibleSection = () => {
    const { showMasterVenue } = useContext(EnquiryFormContext);
    const sectionSchema = useSectionSchema();
    const {
        formState: { isDirty, defaultValues },
        getValues,
        subscribe,
    } = useFormContext<FormValidationType>();

    const { isOpen, toggle, close, open } = useOpenClose(true);
    const fields = useSectionFields();

    // Expand section on submiting if any field is invalid
    useEffect(() => {
        subscribe({
            name: fields,
            callback: ({ isSubmitting, values }) => {
                if (isSubmitting) { // Expand section only on submit attempt
                    const result = sectionSchema.safeParse(values);
                    result.success || open();
                }
            },
            formState: {
                isValidating: true,
            },
        });
    }, [subscribe, sectionSchema, open]);

    // Collapse section if all required fields are filled and didn't touched
    useEffect(() => {
        if (isDirty) return; // We not collapsing section if user changed any field
        const values = getValues();
        const result = sectionSchema.safeParse(values);
        if (result?.success) close();
    }, [
        // defaultValues required to check that default form state is valid.
        // We are loading default values from the server and it's always not valid on the first render
        defaultValues,
        isDirty,
        close,
        showMasterVenue,
        getValues,
        sectionSchema,
    ]);

    return {
        isOpen,
        toggle,
        close,
    }
}

const useIncompleteState = () => {
    const {
        formState: { errors },
    } = useFormContext<FormValidationType>();

    const fields = useSectionFields();

    const isIncomplete = Object.keys(errors).some((key) => fields.includes(key as keyof FormValidationType));
    return isIncomplete;
}

function EnquiryFormUserDetails({
    redirectAnchor
}: {
    redirectAnchor?: string;
}) {
    const { showMasterVenue, isEngagementVenue, hideGuest } = useContext(EnquiryFormContext);
    const {
        control, register,
        formState: { errors },
        setValue,
        watch,
        trigger,
    } = useFormContext<FormValidationType>();

    const engagementDate = watch("engagementDate");
    const weddingDate = watch("date");

    const isIncompleteSection = useIncompleteState();
    const { isOpen, toggle, close } = useCollapsibleSection();


    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const engagementMaxDate = weddingDate ? new Date(weddingDate) : undefined;
    engagementMaxDate?.setDate(engagementMaxDate.getDate() - 1);

    const weddingMinDate = engagementDate ? new Date(engagementDate) : tomorrow;
    weddingMinDate?.setDate(weddingMinDate.getDate() + 1);

    return (
        <div className={cl(css.container, { [css.collapsed]: !isOpen })}>
            <div className={css.header} onClick={toggle}>
                <span>Your details{isIncompleteSection && <span className={css.hint}>(incomplete)</span>}</span>
                {isOpen ? <Icon icon={faMinus} /> : <Icon icon={faPlus} />}
            </div>
            <ClientOnly<UserSectionProps> Component={UserSection} redirectAnchor={redirectAnchor} />
            <InputGroup postfix={errors.name
                ? errors.name.message
                : null
            }>
                <UserIcon />
                <Input
                    id="enquiry-form-name"
                    placeholder="Name"
                    isInvalid={!!errors.name}
                    data-testid="enquiry-form-user-name"
                    {...register("name", {
                        required: "Enter your full name",
                    })}
                />
            </InputGroup>
            <InputGroup postfix={errors.email
                ? errors.email.message
                : null
            }>
                <EmailIcon />
                <Input
                    id="enquiry-form-email"
                    placeholder="Email"
                    isInvalid={!!errors.email}
                    data-testid="enquiry-form-user-email"
                    {...register("email", {
                        required: "Email is required",
                        pattern: {
                            value: /^\S+@\S+$/i,
                            message: "Invalid email address",
                        },
                    })}
                />
            </InputGroup>
            <InputGroup postfix={errors.phone
                ? errors.phone.message
                : null
            }>
                <PhoneIcon />
                <Input
                    id="enquiry-form-phone"
                    placeholder="Phone"
                    isInvalid={!!errors.phone}
                    data-testid="enquiry-form-user-phone"
                    {...register("phone")}
                />
            </InputGroup>

            {isEngagementVenue && <InputGroup postfix={errors.engagementDate
                ? errors.engagementDate.message
                : null
            }>
                <Controller
                    name="engagementDate"
                    control={control}
                    render={({ field }) => (
                        <Input.Date
                            id="enquiry-form-engagement-date"
                            className="text-start"
                            isEmpty={!engagementDate}
                            placeholder="Engagement date"
                            isInvalid={!!errors.engagementDate}
                            data-testid="enquiry-form-user-engagement-date"
                            type="date"
                            minDate={tomorrow}
                            maxDate={engagementMaxDate}
                            {...field}
                            value={engagementDate ? formatDate(engagementDate) : ''}
                            onChange={(date: any) => {
                                if (date instanceof Date) {
                                    setValue("engagementDate", new Date(formatDate(date)));
                                    trigger("engagementDate");
                                }
                            }}
                        />
                    )} />
            </InputGroup>}
            <div className={hideGuest ? undefined : css.twoFields}>
                {!hideGuest && <InputGroup className={css.groupHeight} postfix={errors.guests
                    ? errors.guests.message
                    : null
                }>
                    <PeopleIcon />
                    <Input
                        id="enquiry-form-guests"
                        placeholder="Guests"
                        type="number"
                        isInvalid={!!errors.guests}
                        data-testid="enquiry-form-user-guest-count"
                        style={{ maxWidth: '8ch' }}
                        {...register("guests")}
                    />
                </InputGroup>}
                <InputGroup className={css.groupHeight} postfix={errors.date
                    ? errors.date.message
                    : null
                }>
                    <Controller
                        name="date"
                        control={control}
                        render={({ field }) => (
                            <Input.Date
                                id="enquiry-form-date"
                                className={`text-start ${!weddingDate ? 'ew-input-empty' : ''}`}
                                placeholder="Wedding date"
                                isInvalid={!!errors.date}
                                data-testid="enquiry-form-user-wedding-date"
                                type="date"
                                minDate={weddingMinDate}
                                {...field}
                                value={weddingDate ? formatDate(weddingDate) : ''}
                                onChange={(date: any) => {
                                    if (date instanceof Date) {
                                        setValue("date", new Date(formatDate(date)));
                                        trigger("date");
                                    }
                                }}
                            />
                        )} />
                </InputGroup>
            </div>

            {showMasterVenue && <div>
                <MasterVenueSection />
            </div>}
        </div>
    );
}

function MasterVenueSection() {
    const {
        watch,
    } = useFormContext<FormValidationType>();

    return (<>
        <BookedVenue
        />
        <div style={{ display: 'none' }}>
            <Input
                id="enquiry-form-region"
                type="hidden"
                value={watch("venue.regionId") ?? ''}
            />
            <Input
                id="enquiry-form-region-name"
                type="hidden"
                value={watch("venue.regionName") ?? ''}
            />
            <Input
                id="enquiry-form-venue-name"
                type="hidden"
                value={watch("venue.venueName") ?? ''}
            />
            <Input
                id="enquiry-form-venue-id"
                type="hidden"
                value={watch("venue.venueId") ?? ''}
            />
        </div>
    </>)
}

export default EnquiryFormUserDetails;
